import { db, auth } from '@/firebase/config'
import {
  doc,
  setDoc,
  getDoc,
  collection,
  serverTimestamp,
  getDocs,
  updateDoc,
  arrayUnion,
  query,
  where
} from 'firebase/firestore'
import { saveUserNotification } from './notificationService'
import { getChildProfile } from './parentService'
import { getChildMissionNotification } from '@/utils/staticData/childMissionNotification'
import { getOrCreateAnonId, getDeviceType } from '@/utils/trackingSpine'
import { trackBuyingJourney } from './customerJourneyService'
import {
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS
} from '@/constants/buyingJourney'
import {
  applyEndToAttempts,
  applyEndToUnfinishedAttempts,
  buildAttemptStartData,
  mapAttemptForAnalytics
} from '@/utils/missionAttemptSession'

function getJourneyContext() {
  if (typeof window === 'undefined') return {}
  const anonId = getOrCreateAnonId()
  return {
    path: window.location?.pathname || '',
    anon_id: anonId,
    anonymousId: anonId,
    device_type: getDeviceType()
  }
}

/**
 * Save mission progress for a child
 * @param {Object} missionData - Mission data to save
 * @param {string} childId - The child's ID from the selected profile
 */
export const saveChildMissionProgress = async (missionData, childId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  if (!childId) {
    throw new Error('Child ID is required')
  }

  // Use a composite key of childId and missionId to ensure unique documents
  const docId = `${childId}_${missionData.missionId}`
  const childMissionRef = doc(db, 'childMissionProgress', docId)

  // Check if document exists to detect completion status change
  const docSnap = await getDoc(childMissionRef)
  const existingData = docSnap.exists() ? docSnap.data() : null
  const wasComplete = existingData?.isComplete === true
  const isNowComplete = missionData.isComplete === true
  const justCompleted = !wasComplete && isNowComplete

  const newChildMission = {
    ...missionData,
    childId: childId,
    updatedAt: serverTimestamp()
  }

  if (docSnap.exists() && existingData?.parentUserId) {
    newChildMission.parentUserId = existingData.parentUserId
  } else {
    newChildMission.parentUserId = user.uid
  }

  if (!docSnap.exists()) {
    newChildMission.createdAt = serverTimestamp()
  }

  await setDoc(childMissionRef, newChildMission, { merge: true })

  if (justCompleted && missionData.missionId) {
    try {
      trackBuyingJourney({
        stage: JOURNEY_FUNNEL_STAGES.J_RETENTION,
        eventName: JOURNEY_FUNNEL_EVENTS.MISSION_COMPLETE,
        userId: user.uid,
        missionId: missionData.missionId,
        missionTitle:
          missionData.missionTitle ||
          missionData.missionName ||
          existingData?.missionTitle ||
          existingData?.missionName,
        childId
      })
      const isMission1 = Number(missionData.missionId) === 1
      if (isMission1) {
        trackBuyingJourney({
          stage: JOURNEY_FUNNEL_STAGES.H_ACTIVATION,
          eventName: JOURNEY_FUNNEL_EVENTS.ACTIVATION_AHA,
          userId: user.uid,
          missionId: missionData.missionId,
          childId,
          first_mission_complete: true,
          dedupeKey: 'activation_first_mission_completed',
          ...getJourneyContext()
        })
      }
    } catch (_) {}
  }

  if (justCompleted && missionData.missionId) {
    try {
      // Get mission title from existing data or missionData
      const missionTitle =
        missionData.missionTitle ||
        missionData.missionName ||
        existingData?.missionTitle ||
        existingData?.missionName ||
        'Mission'

      // Check if notification already exists to prevent duplicates
      const notificationId = `mission_complete_${childId}_${missionData.missionId}`
      const notificationsRef = collection(db, 'notifications')
      const notificationQuery = query(
        notificationsRef,
        where('userId', '==', user.uid),
        where('metadata.notificationId', '==', notificationId)
      )
      const existingNotifications = await getDocs(notificationQuery)

      // Only send notification if it doesn't already exist
      if (existingNotifications.empty) {
        // Get child profile to get first name
        let childFirstName = 'Your child'
        try {
          const childProfile = await getChildProfile(user.uid, childId)
          if (childProfile?.firstName) {
            childFirstName = childProfile.firstName
          }
        } catch (error) {
          console.error('Error fetching child profile:', error)
        }

        // Get notification data from static data based on mission ID
        const missionIdNum = parseInt(missionData.missionId, 10)
        const notificationData = getChildMissionNotification(
          missionIdNum,
          'completion',
          childFirstName
        )

        // Use notification from static data if available, otherwise use fallback
        const notificationType = notificationData?.type || 'MISSION_COMPLETED'
        const notificationBody =
          notificationData?.message ||
          `${childFirstName} completed ${missionTitle}!`

        // Send notification to parent
        await saveUserNotification(user.uid, 'PARENT', {
          type: notificationType,
          body: notificationBody,
          metadata: {
            notificationType: 'childMissionComplete',
            notificationId: notificationId,
            missionId: missionData.missionId,
            missionTitle: missionTitle,
            childId: childId
          },
          channel: 'in_app',
          hasAction: true,
          action: {
            label: 'View Progress',
            url: `/dashboard/parent/manage-lessons/${childId}`
          },
          actionUrl: `/dashboard/parent/manage-lessons/${childId}`
        })
      }
    } catch (notificationError) {
      // Don't fail the mission save if notification fails
      console.error(
        'Error sending mission completion notification:',
        notificationError
      )
    }
  }
}

/**
 * Track when a child starts a mission attempt
 * @param {string} missionId - The mission ID
 * @param {string} missionTitle - The mission title
 * @param {string} childId - The child's ID from the selected profile
 * @param {string} [parentUserId] - Parent's uid when child is playing (from missionContext.parentUserId)
 */
export const startChildMissionAttempt = async (
  missionId,
  missionTitle,
  childId,
  parentUserId = null
) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  if (!childId) {
    throw new Error('Child ID is required')
  }

  const effectiveParentUserId = parentUserId || user.uid

  try {
    const docId = `${childId}_${missionId}`
    const childMissionRef = doc(db, 'childMissionProgress', docId)
    const docSnap = await getDoc(childMissionRef)

    const attemptData = buildAttemptStartData()

    const isFirstAttempt = !docSnap.exists()

    if (docSnap.exists()) {
      const existingData = docSnap.data()
      const currentAttemptCount = existingData.attemptCount || 0

      // Update existing document
      await updateDoc(childMissionRef, {
        missionTitle: missionTitle,
        attemptCount: currentAttemptCount + 1,
        attempts: arrayUnion(attemptData),
        lastAttemptStartTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    } else {
      const newDoc = {
        missionId: Number(missionId),
        missionTitle: missionTitle,
        childId: childId,
        parentUserId: effectiveParentUserId,
        step: 'intro_scene',
        progress: 0,
        attemptCount: 1,
        attempts: [attemptData],
        lastAttemptStartTime: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      await setDoc(childMissionRef, newDoc)
    }

    if (isFirstAttempt && Number(missionId) === 1) {
      try {
        trackBuyingJourney({
          stage: JOURNEY_FUNNEL_STAGES.H_ACTIVATION,
          eventName: JOURNEY_FUNNEL_EVENTS.ACTIVATION_FIRST_MISSION_STARTED,
          userId: effectiveParentUserId || user.uid,
          missionId: 1,
          childId,
          dedupeKey: 'activation_first_mission_started',
          ...getJourneyContext()
        })
      } catch (_) {}
    }

    // Send notification to parent when child starts a mission (only on first attempt)
    if (isFirstAttempt) {
      try {
        // Check if notification already exists to prevent duplicates
        const notificationId = `mission_start_${childId}_${missionId}`
        const notificationsRef = collection(db, 'notifications')
        const notificationQuery = query(
          notificationsRef,
          where('userId', '==', user.uid),
          where('metadata.notificationId', '==', notificationId)
        )
        const existingNotifications = await getDocs(notificationQuery)

        // Only send notification if it doesn't already exist
        if (existingNotifications.empty) {
          // Get child profile to get first name
          let childFirstName = 'Your child'
          try {
            const childProfile = await getChildProfile(user.uid, childId)
            if (childProfile?.firstName) {
              childFirstName = childProfile.firstName
            }
          } catch (error) {
            console.error('Error fetching child profile:', error)
          }

          // Get notification data from static data based on mission ID
          const missionIdNum = parseInt(missionId, 10)
          const notificationData = getChildMissionNotification(
            missionIdNum,
            'start',
            childFirstName
          )

          // Use notification from static data if available, otherwise use fallback
          const notificationType = notificationData?.type || 'MISSION_STARTED'
          const notificationBody =
            notificationData?.message ||
            `${childFirstName} just started ${missionTitle}!`

          // Send notification to parent
          await saveUserNotification(user.uid, 'PARENT', {
            type: notificationType,
            body: notificationBody,
            metadata: {
              notificationType: 'childMissionStart',
              notificationId: notificationId,
              missionId: missionId,
              missionTitle: missionTitle,
              childId: childId
            },
            channel: 'in_app',
            hasAction: true,
            action: {
              label: 'View Progress',
              url: `/dashboard/parent/manage-lessons/${childId}`
            },
            actionUrl: `/dashboard/parent/manage-lessons/${childId}`
          })
        }
      } catch (notificationError) {
        // Don't fail the mission start if notification fails
        console.error(
          'Error sending mission start notification:',
          notificationError
        )
      }
    }

    return attemptData.startTime
  } catch (error) {
    console.error('Error starting child mission attempt:', error)
    throw error
  }
}

/**
 * Track when a child ends a mission attempt
 * @param {string} missionId - The mission ID
 * @param {Date} startTime - The start time of the session
 * @param {string} childId - The child's ID from the selected profile
 */
export const endChildMissionAttempt = async (
  missionId,
  startTime,
  childId,
  endMeta = {}
) => {
  const user = auth.currentUser
  if (!user || !startTime || !childId) {
    return { ok: false, skipped: true }
  }

  try {
    const docId = `${childId}_${missionId}`
    const childMissionRef = doc(db, 'childMissionProgress', docId)
    const docSnap = await getDoc(childMissionRef)

    if (!docSnap.exists()) {
      return { ok: true, skipped: true }
    }

    const data = docSnap.data()
    const result = applyEndToAttempts(data.attempts || [], startTime, endMeta)

    if (!result.changed) {
      return { ok: true, skipped: true }
    }

    await updateDoc(childMissionRef, {
      attempts: result.attempts,
      totalTimeSpent: result.totalTimeSpent,
      lastSessionDuration: result.duration,
      updatedAt: serverTimestamp()
    })

    return { ok: true }
  } catch (error) {
    console.error('Error ending child mission attempt:', error)
    return { ok: false, error }
  }
}

export const finalizeUnfinishedChildMissionAttempts = async (
  missionId,
  childId,
  endMeta = {}
) => {
  const user = auth.currentUser
  if (!user || !missionId || !childId) {
    return { ok: false, skipped: true }
  }

  try {
    const docId = `${childId}_${missionId}`
    const childMissionRef = doc(db, 'childMissionProgress', docId)
    const docSnap = await getDoc(childMissionRef)

    if (!docSnap.exists()) {
      return { ok: true, skipped: true }
    }

    const data = docSnap.data()
    const result = applyEndToUnfinishedAttempts(data.attempts || [], endMeta)

    if (!result.changed) {
      return { ok: true, skipped: true }
    }

    await updateDoc(childMissionRef, {
      attempts: result.attempts,
      totalTimeSpent: result.totalTimeSpent,
      lastSessionDuration: result.duration,
      updatedAt: serverTimestamp()
    })

    return { ok: true }
  } catch (error) {
    console.error('Error finalizing unfinished child mission attempts:', error)
    return { ok: false, error }
  }
}

/**
 * Get mission analytics data for a child
 * @param {string} missionId - The mission ID
 * @param {string} childId - The child's ID from the selected profile
 */
export const getChildMissionAnalytics = async (missionId, childId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  if (!childId) {
    throw new Error('Child ID is required')
  }

  try {
    const docId = `${childId}_${missionId}`
    const childMissionRef = doc(db, 'childMissionProgress', docId)
    const docSnap = await getDoc(childMissionRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      const attempts = data.attempts || []

      // Calculate analytics
      const completedAttempts = attempts.filter(
        (attempt) => attempt.endTime
      ).length
      const totalDuration = attempts.reduce(
        (total, attempt) => total + (attempt.duration || 0),
        0
      )
      const averageDuration =
        completedAttempts > 0 ? totalDuration / completedAttempts : 0

      return {
        attemptCount: data.attemptCount || 0,
        completedAttempts,
        totalTimeSpent: totalDuration,
        averageSessionDuration: averageDuration,
        attempts: attempts.map((attempt) => mapAttemptForAnalytics(attempt)),
        lastAttempt: data.lastAttemptStartTime?.toDate?.() || null
      }
    }

    return {
      attemptCount: 0,
      completedAttempts: 0,
      totalTimeSpent: 0,
      averageSessionDuration: 0,
      attempts: [],
      lastAttempt: null
    }
  } catch (error) {
    console.error('Error fetching child mission analytics:', error)
    throw error
  }
}

/**
 * Get mission progress for a specific child and mission
 * @param {string} missionId - The mission ID
 * @param {string} childId - The child's ID from the selected profile
 */
export const getChildMissionProgress = async (missionId, childId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  if (!childId) {
    throw new Error('Child ID is required')
  }

  try {
    const docId = `${childId}_${missionId}`
    const childMissionRef = doc(db, 'childMissionProgress', docId)
    const docSnap = await getDoc(childMissionRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      return data
    } else {
      // Return default progress if no document exists
      return {
        missionId: missionId,
        childId: childId,
        parentUserId: user.uid,
        step: 'intro_scene',
        progress: 0,
        attemptCount: 0,
        totalTimeSpent: 0,
        attempts: []
      }
    }
  } catch (error) {
    console.error('Error fetching child mission progress:', error)
    throw error
  }
}

/**
 * Get all mission progress for a specific child
 * @param {string} childId - The child's ID from the selected profile
 */
export const getChildMissionsProgress = async (childId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  if (!childId) {
    throw new Error('Child ID is required')
  }

  try {
    const childMissionsRef = collection(db, 'childMissionProgress')
    const q = query(
      childMissionsRef,
      where('childId', '==', childId),
      where('parentUserId', '==', user.uid)
    )
    const docSnap = await getDocs(q)

    const missionsProgress = docSnap.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        // Include analytics data in the response
        analytics: {
          attemptCount: data.attemptCount || 0,
          totalTimeSpent: data.totalTimeSpent || 0,
          lastAttempt: data.lastAttemptStartTime?.toDate?.() || null,
          averageSessionDuration:
            data.attempts?.length > 0
              ? (data.totalTimeSpent || 0) / data.attempts.length
              : 0
        }
      }
    })

    return missionsProgress
  } catch (error) {
    console.error('Error fetching child missions progress:', error)
    throw error
  }
}

/**
 * Get all mission progress for all children of the current parent
 */
export const getAllChildrenMissionsProgress = async () => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const childMissionsRef = collection(db, 'childMissionProgress')
    const q = query(childMissionsRef, where('parentUserId', '==', user.uid))
    const docSnap = await getDocs(q)

    const missionsProgress = docSnap.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        // Include analytics data in the response
        analytics: {
          attemptCount: data.attemptCount || 0,
          totalTimeSpent: data.totalTimeSpent || 0,
          lastAttempt: data.lastAttemptStartTime?.toDate?.() || null,
          averageSessionDuration:
            data.attempts?.length > 0
              ? (data.totalTimeSpent || 0) / data.attempts.length
              : 0
        }
      }
    })

    return missionsProgress
  } catch (error) {
    console.error('Error fetching all children missions progress:', error)
    throw error
  }
}

/**
 * Update instruction video watched status for a child
 * @param {string|number} missionId - The mission ID
 * @param {string} childId - The child's ID from the selected profile
 */
export const updateChildInstructionVideoWatched = async (
  missionId,
  childId
) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  if (!childId) {
    throw new Error('Child ID is required')
  }

  try {
    const docId = `${childId}_${missionId}`
    const childMissionRef = doc(db, 'childMissionProgress', docId)

    await setDoc(
      childMissionRef,
      {
        hasWatched: true,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    )

    return true
  } catch (error) {
    console.error('Error updating child instruction video status:', error)
    throw error
  }
}

/**
 * Reset child mission progress to 0 and step to intro_scene without deleting the document
 * This preserves analytics, attemptCount, attempts, and other metadata
 * @param {string|number} missionId - The mission ID
 * @param {string} childId - The child's ID from the selected profile
 */
export const resetChildMissionProgress = async (missionId, childId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  if (!childId) {
    throw new Error('Child ID is required')
  }

  try {
    const docId = `${childId}_${missionId}`
    const childMissionRef = doc(db, 'childMissionProgress', docId)
    const docSnap = await getDoc(childMissionRef)

    if (docSnap.exists()) {
      await updateDoc(childMissionRef, {
        progress: 0,
        step: 'intro_scene',
        isComplete: false,
        updatedAt: serverTimestamp()
      })
    } else {
      // If document doesn't exist, create it with default values
      await setDoc(childMissionRef, {
        missionId: missionId,
        childId: childId,
        parentUserId: user.uid,
        step: 'intro_scene',
        progress: 0,
        isComplete: false,
        attemptCount: 0,
        attempts: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error resetting child mission progress:', error)
    throw error
  }
}
